import { useEffect, useState } from 'react'
import { ArrowUpRight, PlayCircle, Video, ListVideo } from 'lucide-react'
import { Link } from 'react-router-dom'
import { listCreatorSeries, type CreatorSeriesSummary } from '@/lib/api/creator'

export function CreatorDashboard() {
  const [series, setSeries] = useState<CreatorSeriesSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    listCreatorSeries()
      .then((items) => {
        if (cancelled) return
        setSeries(items)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Failed to load creator data')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [])

  const totalSeries = series.length
  const totalEpisodes = series.reduce((sum, s) => sum + (s.episodeCount ?? 0), 0)

  const drafts = series.filter((s) => s.status === 'draft')
  const pending = series.filter((s) => s.status === 'pending_review')
  const approved = series.filter((s) => s.status === 'approved')
  const rejected = series.filter((s) => s.status === 'rejected')

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">Creator overview</h1>
          <p className="mt-1 text-sm text-gray-400">
            High-level snapshot of your vertical dramas powered by your real creator data.
          </p>
        </div>
        <div className="flex flex-wrap gap-3 text-sm">
          <Link
            to="/creator/upload"
            className="inline-flex items-center gap-2 rounded-lg bg-[#D72638] px-4 py-2 font-medium text-white hover:bg-[#D72638]/90"
          >
            <PlayCircle className="w-4 h-4" />
            Upload new episode
          </Link>
          <Link
            to="/creator/series"
            className="inline-flex items-center gap-2 rounded-lg border border-gray-800 px-4 py-2 font-medium text-gray-200 hover:bg-white/5"
          >
            <Video className="w-4 h-4" />
            Manage series
          </Link>
        </div>
      </div>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-xl border border-gray-800 bg-gradient-to-br from-gray-900/80 to-black px-4 py-4">
          <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Total series</p>
          <p className="text-2xl font-semibold text-white mb-1">
            {loading ? '—' : totalSeries}
          </p>
          <p className="text-xs text-gray-400">Series you are actively publishing</p>
        </div>
        <div className="rounded-xl border border-gray-800 bg-gradient-to-br from-gray-900/80 to-black px-4 py-4">
          <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Total episodes</p>
          <p className="text-2xl font-semibold text-white mb-1">
            {loading ? '—' : totalEpisodes}
          </p>
          <p className="text-xs text-gray-400">Across all your series</p>
        </div>
        <div className="rounded-xl border border-gray-800 bg-gradient-to-br from-gray-900/80 to-black px-4 py-4">
          <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Creator stats</p>
          <p className="text-2xl font-semibold text-white mb-1">Coming soon</p>
          <p className="text-xs text-gray-400">Detailed views and watch-time analytics</p>
        </div>
        <div className="rounded-xl border border-gray-800 bg-gradient-to-br from-gray-900/80 to-black px-4 py-4">
          <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Upcoming premieres</p>
          <p className="text-2xl font-semibold text-white mb-1">Coming soon</p>
          <p className="text-xs text-gray-400">Next scheduled episode drops</p>
        </div>
      </section>

      {error && (
        <div className="rounded-lg border border-red-700 bg-red-900/30 px-3 py-2 text-xs text-red-200">
          {error}
        </div>
      )}

      <section className="grid gap-6 md:grid-cols-[minmax(0,2fr)_minmax(0,1.4fr)]">
        <div className="rounded-xl border border-gray-800 bg-gradient-to-br from-gray-900/80 to-black p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-white">Uploads in progress</h2>
            <span className="inline-flex items-center gap-1 text-[11px] text-gray-400">
              <ArrowUpRight className="w-3 h-3" />
              From this browser only
            </span>
          </div>
          <p className="text-sm text-gray-400">
            Uploads started from the web studio will appear here in a future update. For now, use the
            Upload tab to start new episodes.
          </p>
        </div>

        <div className="rounded-xl border border-gray-800 bg-gradient-to-br from-gray-900/80 to-black p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-white">Next steps</h2>
          </div>
          <ul className="space-y-2 text-sm text-gray-300">
            <li className="flex items-start gap-2">
              <Video className="mt-0.5 w-3.5 h-3.5 text-[#D4AF37]" />
              <span>Create a new series from the{' '}
                <Link to="/creator/series" className="text-[#D4AF37] hover:underline">
                  Series
                </Link>{' '}
                tab.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <ListVideo className="mt-0.5 w-3.5 h-3.5 text-[#D4AF37]" />
              <span>
                Upload your first batch of vertical episodes from the{' '}
                <Link to="/creator/upload" className="text-[#D4AF37] hover:underline">
                  Upload
                </Link>{' '}
                tab.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <PlayCircle className="mt-0.5 w-3.5 h-3.5 text-[#D4AF37]" />
              <span>
                Once wired to the backend, this dashboard will show real performance metrics for your series.
              </span>
            </li>
          </ul>
        </div>
      </section>

      {!loading && series.length > 0 && (
        <section className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-white">My series</h2>
            <Link
              to="/creator/series"
              className="text-xs text-[#D4AF37] hover:text-[#FFD700]"
            >
              View all
            </Link>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-3">
              <h3 className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Drafts
              </h3>
              {drafts.length === 0 ? (
                <p className="text-xs text-gray-500">
                  No draft series yet. Start a new idea from the Series tab.
                </p>
              ) : (
                <div className="space-y-2">
                  {drafts.slice(0, 4).map((s) => (
                    <Link
                      key={s.id}
                      to={`/creator/series/${s.id}`}
                      className="flex items-center justify-between gap-3 rounded-lg border border-gray-800 bg-black/40 px-3 py-2 hover:border-[#D4AF37]/60 hover:bg-white/5 transition-colors"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-white truncate">{s.title}</p>
                        <p className="text-[11px] text-gray-500">
                          {s.episodeCount ?? 0} episode{s.episodeCount === 1 ? '' : 's'}
                        </p>
                      </div>
                      <span className="inline-flex items-center rounded-full bg-yellow-900/30 px-2 py-0.5 text-[11px] font-medium text-yellow-200 border border-yellow-700">
                        Draft
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-3">
              <h3 className="text-xs font-medium uppercase tracking-wide text-gray-500">
                In review
              </h3>
              {pending.length === 0 ? (
                <p className="text-xs text-gray-500">
                  Nothing in review right now. Submit a draft to see it here.
                </p>
              ) : (
                <div className="space-y-2">
                  {pending.slice(0, 4).map((s) => (
                    <Link
                      key={s.id}
                      to={`/creator/series/${s.id}`}
                      className="flex items-center justify-between gap-3 rounded-lg border border-gray-800 bg-black/40 px-3 py-2 hover:border-[#D4AF37]/60 hover:bg-white/5 transition-colors"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-white truncate">{s.title}</p>
                        <p className="text-[11px] text-gray-500">
                          {s.episodeCount ?? 0} episode{s.episodeCount === 1 ? '' : 's'}
                        </p>
                      </div>
                      <span className="inline-flex items-center rounded-full bg-[#D4AF37]/15 px-2 py-0.5 text-[11px] font-medium text-[#D4AF37] border border-[#D4AF37]/60">
                        Pending review
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-3">
              <h3 className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Published
              </h3>
              {approved.length === 0 ? (
                <p className="text-xs text-gray-500">
                  No published series yet. Once approved, they will appear here.
                </p>
              ) : (
                <div className="space-y-2">
                  {approved.slice(0, 4).map((s) => (
                    <Link
                      key={s.id}
                      to={`/creator/series/${s.id}`}
                      className="flex items-center justify-between gap-3 rounded-lg border border-gray-800 bg-black/40 px-3 py-2 hover:border-emerald-600/70 hover:bg-emerald-900/10 transition-colors"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-white truncate">{s.title}</p>
                        <p className="text-[11px] text-gray-500">
                          {s.episodeCount ?? 0} episode{s.episodeCount === 1 ? '' : 's'}
                        </p>
                      </div>
                      <span className="inline-flex items-center rounded-full bg-emerald-900/30 px-2 py-0.5 text-[11px] font-medium text-emerald-300 border border-emerald-700">
                        Published
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-3">
              <h3 className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Rejected
              </h3>
              {rejected.length === 0 ? (
                <p className="text-xs text-gray-500">
                  No rejected series. If anything needs changes, it will show up here.
                </p>
              ) : (
                <div className="space-y-2">
                  {rejected.slice(0, 4).map((s) => (
                    <Link
                      key={s.id}
                      to={`/creator/series/${s.id}`}
                      className="flex items-center justify-between gap-3 rounded-lg border border-gray-800 bg-black/40 px-3 py-2 hover:border-red-600/70 hover:bg-red-900/10 transition-colors"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-white truncate">{s.title}</p>
                        <p className="text-[11px] text-gray-500">
                          {s.episodeCount ?? 0} episode{s.episodeCount === 1 ? '' : 's'}
                        </p>
                      </div>
                      <span className="inline-flex items-center rounded-full bg-red-900/30 px-2 py-0.5 text-[11px] font-medium text-red-300 border border-red-700">
                        Rejected
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      )}
    </div>
  )
}

